import { inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { provideI18n, readStoredAppLang, type AppLang, type I18nResources } from 'internalization';

import { AppComponent } from '@/app/entry-point/app.component';
import { shouldShowPwaOfflineFallback } from '@/app/entry-point/pwa-offline-start';
import { APP_ROUTES } from '@/app/routes/app.routes';
import { JMIX_UI_URL, PYTHON_URL } from '@/shared/config/base-url.token';
import { environment } from '@/shared/config/environments/environment';
import { ProductionReadyEnum } from '@/shared/config/production-ready.enum';
import { authInterceptor } from '@/shared/interceptors/auth.interceptor';
import { paymentRequiredInterceptor } from '@/shared/interceptors/payment-required.interceptor';
import { AuthService } from '@/shared/services/auth.service';
import { PwaInstallPromptService } from '@/shared/services/pwa-install-prompt.service';
import { VersionCheckService } from '@/shared/services/version-check.service';

async function loadI18nBundle(lang: AppLang): Promise<unknown> {
  const module = await import(`../../shared/assets/i18n/${lang}.json`);
  const value: unknown = (module as { default?: unknown }).default ?? module;
  return value;
}

async function getI18nResources(): Promise<Partial<I18nResources>> {
  // Холодный старт тянет только активный язык + ru-фолбэк; остальные 9 словарей
  // догружаются ленивыми чанками через I18nBundleService.ensure() при смене языка.
  const initialLang = readStoredAppLang();
  const ruResources = await import('@/shared/assets/i18n/ru.json');
  if (initialLang === 'ru') {
    return { ruResources };
  }
  return { ruResources, [`${initialLang}Resources`]: await loadI18nBundle(initialLang) } as Partial<I18nResources>;
}

function getJmixUiUrl(): string {
  const url = window.location.origin;
  if (environment.production) {
    return `${url}/${ProductionReadyEnum.JmixUi}`;
  }

  return 'http://localhost:8080/jmix';
}

function getPythonUrl(): string {
  const url = window.location.origin;
  if (environment.production) {
    return `${url}/${ProductionReadyEnum.Python}`;
  }

  if (environment.useLocalProxy) {
    return '/api'; // В локальном режиме префикс /api проксируется на бэкенд
  }

  return 'http://localhost:8900';
}

async function shouldShowOfflineFallback(): Promise<boolean> {
  const appleNavigator = window.navigator as Navigator & { standalone?: boolean };
  const standalone = appleNavigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
  return shouldShowPwaOfflineFallback(
    environment.production,
    window.navigator.onLine,
    standalone,
    document.baseURI,
    fetch,
  );
}

async function bootstrapIdoc(): Promise<void> {
  if (await shouldShowOfflineFallback()) {
    const { PwaOfflineComponent } = await import('@/shared/components/pwa-offline/pwa-offline.component');
    await bootstrapApplication(PwaOfflineComponent, {
      providers: [provideZonelessChangeDetection(), provideI18n(getI18nResources, loadI18nBundle)],
    });
    return;
  }

  await bootstrapApplication(AppComponent, {
    providers: [
      { provide: JMIX_UI_URL, useFactory: getJmixUiUrl },
      { provide: PYTHON_URL, useFactory: getPythonUrl },
      provideZonelessChangeDetection(),
      provideHttpClient(withFetch(), withInterceptors([authInterceptor, paymentRequiredInterceptor])),
      provideAppInitializer(() => inject(PwaInstallPromptService).initialize()),
      provideAppInitializer(() => inject(VersionCheckService).start()),
      provideAppInitializer(() => inject(AuthService).checkSession()),
      provideI18n(getI18nResources, loadI18nBundle),
      provideRouter(APP_ROUTES),
      provideServiceWorker('ngsw-worker.js', {
        // Управляется environment-флагом (а не isDevMode): локальная конфигурация
        // включает SW для проверки Web Push, обычный `nx serve` — нет.
        enabled: environment.serviceWorkerEnabled,
        registrationStrategy: 'registerWhenStable:30000',
      }),
    ],
  });
}

void bootstrapIdoc().catch(err => console.error(err));
